/**
 * Validation utilities for per-day time range configuration
 */

/**
 * Converts time string (HH:MM) to minutes for comparison
 * @param {string} time - Time in HH:MM format
 * @returns {number} - Time in minutes from midnight
 */
const timeToMinutes = (time) => {
    if (!time) return null;
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
};

/**
 * Check if two time ranges overlap
 * @param {Object} range1 - First time range {start_time, end_time}
 * @param {Object} range2 - Second time range {start_time, end_time}
 * @returns {boolean} - True if ranges overlap
 */
const doRangesOverlap = (range1, range2) => {
    const start1 = timeToMinutes(range1.start_time);
    const end1 = timeToMinutes(range1.end_time);
    const start2 = timeToMinutes(range2.start_time);
    const end2 = timeToMinutes(range2.end_time);

    if (start1 === null || end1 === null || start2 === null || end2 === null) {
        return false;
    }

    // Check if ranges overlap
    return (start1 < end2 && end1 > start2);
};

/**
 * Validate a single time range
 * @param {Object} range - Time range {start_time, end_time}
 * @param {string} globalStartTime - Global campaign start time (optional)
 * @param {string} globalEndTime - Global campaign end time (optional)
 * @returns {string|null} - Error message or null if valid
 */
export const validateTimeRange = (range, globalStartTime = null, globalEndTime = null) => {
    if (!range.start_time && !range.end_time) {
        return 'Both start time and end time are required.';
    }
    if (!range.start_time) {
        return 'Start time is required.';
    }
    if (!range.end_time) {
        return 'End time is required.';
    }

    const startMinutes = timeToMinutes(range.start_time);
    const endMinutes = timeToMinutes(range.end_time);

    if (startMinutes >= endMinutes) {
        return 'Start time must be before end time.';
    }

    // Validate against global time frame if provided
    if (globalStartTime && globalEndTime) {
        const globalStart = timeToMinutes(globalStartTime);
        const globalEnd = timeToMinutes(globalEndTime);

        if (startMinutes < globalStart || endMinutes > globalEnd) {
            return `Time range must be within global campaign time frame (${formatTime(globalStartTime)} - ${formatTime(globalEndTime)}).`;
        }
    }

    return null;
};

/**
 * Format time for display in 24-hour format (e.g., "10:00")
 * @param {string} time - Time in HH:MM format
 * @returns {string} - Formatted time string in 24-hour format
 */
const formatTime = (time) => {
    if (!time) return '';
    // Return time in 24-hour format (HH:MM)
    return time;
};

/**
 * Validate all time ranges for a specific day
 * @param {Array} ranges - Array of time ranges for a day
 * @param {string} globalStartTime - Global campaign start time (optional)
 * @param {string} globalEndTime - Global campaign end time (optional)
 * @param {boolean} requireAtLeastOne - Whether to require at least one time range (default: false)
 * @returns {Array} - Array of error objects {index, message}
 */
export const validateDayTimeRanges = (ranges, globalStartTime = null, globalEndTime = null, requireAtLeastOne = false) => {
    const errors = [];

    // Only require at least one range if explicitly specified
    if (requireAtLeastOne && (!ranges || ranges.length === 0)) {
        errors.push({ message: 'At least one time range is required for this day.' });
        return errors;
    }

    // If no ranges exist and not required, that's valid
    if (!ranges || ranges.length === 0) {
        return errors;
    }

    // Validate each range individually
    ranges.forEach((range, index) => {
        const rangeError = validateTimeRange(range, globalStartTime, globalEndTime);
        if (rangeError) {
            errors.push({ index, message: rangeError });
        }
    });

    // Check for overlaps only if all ranges are valid
    if (errors.length === 0) {
        for (let i = 0; i < ranges.length; i++) {
            for (let j = i + 1; j < ranges.length; j++) {
                if (doRangesOverlap(ranges[i], ranges[j])) {
                    errors.push({
                        index: i,
                        message: `Time range overlaps with another range on this day.`
                    });
                    errors.push({
                        index: j,
                        message: `Time range overlaps with another range on this day.`
                    });
                }
            }
        }
    }

    return errors;
};

/**
 * Validate all Edit Campaign Time Frames by Day
 * @param {Object} dayTimeframes - Object with day names as keys and array of time ranges as values
 * @param {Array} selectedDays - Array of selected day names
 * @param {string} globalStartTime - Global campaign start time (optional)
 * @param {string} globalEndTime - Global campaign end time (optional)
 * @returns {Object} - Object with day names as keys and array of errors as values
 */
export const validateAllDayTimeframes = (dayTimeframes, selectedDays, globalStartTime = null, globalEndTime = null) => {
    const allErrors = {};

    // Normalize selectedDays
    const normalizeSelected = (val) => {
        if (Array.isArray(val)) return val;
        if (typeof val === 'string') return val.split(',').map(d => d.trim()).filter(Boolean);
        return [];
    };

    const selectedArray = normalizeSelected(selectedDays);

    // Validate only days that have time ranges configured
    // Days without time ranges are valid (not required for each day)
    Object.keys(dayTimeframes).forEach((day) => {
        const dayRanges = dayTimeframes[day] || [];

        // Only validate if this day has ranges, or if it's in selected days and has ranges
        if (dayRanges.length > 0) {
            const dayErrors = validateDayTimeRanges(dayRanges, globalStartTime, globalEndTime, false);

            if (dayErrors.length > 0) {
                allErrors[day] = dayErrors;
            }
        }
    });

    return allErrors;
};

/**
 * Check if per-day timeframes have any validation errors
 * @param {Object} errors - Errors object from validateAllDayTimeframes
 * @returns {boolean} - True if there are any errors
 */
export const hasTimeframeErrors = (errors) => {
    return Object.keys(errors).length > 0;
};
